import axios from "axios";
import { config as apiConfig } from "../../config";
import React from "react";
import ReactDOM from "react-dom";
import ErrorState from "./ErrorState";
import ApiWrapper from "./ApiWrapper";
import {
  setTicketData,
  setTicketLoading,
  setTicketError,
  setCompanyData,
  setCompanyLoading,
  setCompanyError,
  markCompanyAsFetched,
} from "../../store/Store";
import i18n from "i18next";
import { adjustmentTypes } from "./constants";
const { t } = i18n;

// Format date string
export const formatDate = (dateString) => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  }).format(date);
};

// Find tip for a specific appointment ID
export const findTipForAppointment = (ticketData, appointmentId) => {
  if (!ticketData?.resource?.adjustments) return null;

  return ticketData.resource.adjustments.find(
    (adj) =>
      adj.adjustable_type === "Tip" &&
      adj.adjustable?.my_client_appointment_id === appointmentId
  );
};

// Fetch ticket data from API
export const fetchTicketData = async (accessToken) => {
  try {
    const { apiBaseUrl } = apiConfig;

    if (!apiBaseUrl) {
      throw new Error("API Base URL is not configured");
    }

    setTicketLoading(true);

    // Remove any trailing slashes from the base URL
    const baseUrl = apiBaseUrl.replace(/\/$/, "");

    // Token is sent in the Authorization header (new path) AND as a query
    // param (legacy fallback) during the LT-450705 rollout. Drop the query
    // param in the follow-up PR after header-path traffic is verified.
    const requestUrl = `${baseUrl}/api/mkp/v1/intake/resource?access_token=${accessToken}`;

    // Create axios config object
    const axiosConfig = {
      url: requestUrl,
      method: "GET",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      validateStatus: function (status) {
        return status >= 200 && status < 300; // default
      },
    };

    const response = await axios(axiosConfig);
    setTicketData(response.data);
    setTicketLoading(false);
  } catch (error) {
    console.error(t("ticket.fetch_error"), error);

    let errorMessage = t("ticket.fetch_error");

    if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.response?.status) {
      errorMessage = `Server error: ${error.response.status} - ${error.response.statusText}`;
    } else if (error.message) {
      errorMessage = `Error: ${error.message}`;
    }

    setTicketError(errorMessage);
    setTicketLoading(false);
  }
};

export const fetchCompanyData = async (companyId) => {
  setCompanyError(null);
  setCompanyLoading(true);

  const { data, error } = await ApiWrapper.get(`/api/mkp/v1/companies/${companyId}`);
  
  if (error) {
    setCompanyError(error.message || "Failed to load company. Please try again.");
    setCompanyLoading(false);
    return;
  }

  setCompanyData(data?.company);
  markCompanyAsFetched(companyId);
  setCompanyLoading(false);
};

// Submit tip to API
export const submitTip = async (accessToken, appointmentId, tipValue) => {
  if (!tipValue || isNaN(parseFloat(tipValue))) {
    return;
  }

  try {
    const { apiBaseUrl } = apiConfig;
    const baseUrl = apiBaseUrl.replace(/\/$/, "");

    const requestUrl = `${baseUrl}/api/mkp/v1/intake/tips`;

    // Token is sent in the Authorization header (new path) AND in the body
    // (legacy fallback) during the LT-450705 rollout. Drop the body field
    // in the follow-up PR after header-path traffic is verified.
    const payload = {
      access_token: accessToken,
      tip: {
        value: parseFloat(tipValue),
        my_client_appointment_id: appointmentId,
      },
    };

    await axios({
      url: requestUrl,
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      data: payload,
    });

    // Refresh ticket data after successful tip submission
    fetchTicketData(accessToken);
  } catch (error) {
    // Create a modal container if it doesn't exist
    let modalContainer = document.getElementById("error-modal-container");
    if (!modalContainer) {
      modalContainer = document.createElement("div");
      modalContainer.id = "error-modal-container";
      document.body.appendChild(modalContainer);
    }

    // Check for ticket_closed error
    if (error.response?.data?.errors?.status?.[0]?.type === "ticket_closed") {
      // Render the ErrorState component as a modal
      ReactDOM.render(
        <ErrorState
          error={error}
          isModal={true}
          title={t("tips.error")}
          message={t("tips.closed_ticket_error_message")}
          buttonText="OK"
          onButtonClick={() => {
            ReactDOM.unmountComponentAtNode(modalContainer);
          }}
          onClose={() => {
            ReactDOM.unmountComponentAtNode(modalContainer);
          }}
        />,
        modalContainer
      );
    } else {
      // For other errors, show a generic message in the modal
      ReactDOM.render(
        <ErrorState
          error={error}
          isModal={true}
          title={t("general.error")}
          message={t("tips.error")}
          buttonText={t("general.ok")}
          onButtonClick={() => {
            ReactDOM.unmountComponentAtNode(modalContainer);
          }}
          onClose={() => {
            ReactDOM.unmountComponentAtNode(modalContainer);
          }}
        />,
        modalContainer
      );
    }
  }
};

export const processAdjustmentsData = (adjustments) => {
  const slotVariationAdjustments = {};
  const productAdjustments = [];
  const membershipAdjustments = [];
  const packageAdjustments = [];
  const giftCardAdjustments = [];
  const sessionEnrollmentAdjustments = [];
  const surchargeAdjustments = [];
  const cancellationFeeAdjustments = [];
  const miscItemAdjustments = [];

  if (!adjustments?.length) {
    return { slotVariationAdjustments, productAdjustments };
  }

  const adjustmentArrayByType = {
    [adjustmentTypes.slotVariation]: slotVariationAdjustments,
    [adjustmentTypes.product]: productAdjustments,
    [adjustmentTypes.membership]: membershipAdjustments,
    [adjustmentTypes.package]: packageAdjustments,
    [adjustmentTypes.giftCard]: giftCardAdjustments,
    [adjustmentTypes.sessionEnrollment]: sessionEnrollmentAdjustments,
    [adjustmentTypes.surchargeItem]: surchargeAdjustments,
    [adjustmentTypes.cancellationFee]: cancellationFeeAdjustments,
    [adjustmentTypes.miscItem]: miscItemAdjustments,
  };

  const taxAdjustments = adjustments.filter(
    (adj) => adj.adjustable_type === adjustmentTypes.tax
  );

  adjustments.forEach((adjustment) => {
    const associatedTaxes = taxAdjustments.filter(
      (adj) =>
        adj.adjustable_type === adjustmentTypes.tax &&
        adj.taxed_ticket_item?.id === adjustment.id
    );

    if (adjustment.adjustable_type === adjustmentTypes.slotVariation) {
      const appointmentId = adjustment.adjustable?.my_client_appointment_id;

      slotVariationAdjustments[appointmentId] =
        slotVariationAdjustments[appointmentId] || [];

      // organize slot variation adjustments and taxes by appointment id
      slotVariationAdjustments[appointmentId]?.push({
        adjustment,
        associatedTaxes,
      });
    } else {
      adjustment.id &&
        adjustmentArrayByType[adjustment.adjustable_type]?.push({
          adjustment,
          associatedTaxes,
        });
    }
  });

  return {
    slotVariationAdjustments,
    productAdjustments,
    membershipAdjustments,
    packageAdjustments,
    giftCardAdjustments,
    sessionEnrollmentAdjustments,
    surchargeAdjustments,
    cancellationFeeAdjustments,
    miscItemAdjustments,
  };
};

const roundToCents = (value) => Math.round((value + Number.EPSILON) * 100) / 100;

// Total of the tip adjustments currently on the ticket, whether they were added
// at the POS before the payment link was generated or on this page afterwards.
export const getTipsTotal = (ticketData) => {
  const adjustments = ticketData?.resource?.adjustments;
  if (!Array.isArray(adjustments)) return 0;

  return roundToCents(
    adjustments
      .filter((adj) => adj?.adjustable_type === adjustmentTypes.tip)
      .reduce((sum, adj) => {
        const value = parseFloat(adj?.amount);
        return sum + (isNaN(value) || value <= 0 ? 0 : value);
      }, 0)
  );
};

// The amount to charge. Links can pin a fixed `amount` in the token payload
// (the mobile app's Send Invoice does this; the web/OpenSlot one does not), and
// that amount is a snapshot of the balance when the link was generated. It is
// never updated afterwards, so a tip added on this page used to be shown in the
// balance but silently excluded from the charge, leaving the tip outstanding.
//
// The pinned amount is only meant to cap the *ticket* portion, not tips. So we
// compare it against the balance excluding tips: if it still covers that in
// full, the link was for the whole ticket and we charge the live balance due
// (which includes whatever tips exist now). Only a genuine partial-payment link
// -- one pinned below the non-tip balance -- keeps its fixed amount.
export const getBalanceDue = (ticketData) => {
  const customAmount = ticketData?.access_token?.data?.payload?.amount;
  const balanceDue = ticketData?.resource?.balance_due || 0;

  if (!customAmount || isNaN(parseFloat(customAmount))) return balanceDue;

  const balanceExcludingTips = roundToCents(balanceDue - getTipsTotal(ticketData));

  return parseFloat(customAmount) < balanceExcludingTips
    ? parseFloat(customAmount)
    : balanceDue;
};

export const capitalize = (str) => {
  if (!str || typeof str !== "string") return "";
  return str.includes(" ")
    ? str.split(" ").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ")
    : str.charAt(0).toUpperCase() + str.slice(1);
}

export const cloud9Funding = (funding = "") => {
  switch(funding.toUpperCase()){
    case "C":
      return "credit";
    case "D":
      return "debit";
    case "U":
      return "credit";
    default:
      return "credit";
  }
}

export const buildCardDetailsForCloud9FromTokenData = (tokenData = {}) => {
  if (!tokenData) return {};

  return {
    funding: (cloud9Funding(tokenData?.CardDebitCreditIndicator) || '').toLowerCase(),
    brand: cloud9CardBrandLabel(tokenData?.Brand),
    last4: (tokenData?.AccountNum || "").slice(-4),
    expDate: tokenData?.ExpDate,
    month: (tokenData?.ExpDate || "").substring(0, 2),
    year: "20" + (tokenData?.ExpDate || "").substring(2), 
  }
}

export const cloud9CardBrandLabel = (brand = "") => {
  const brandMap = {
    Visa: "Visa",
    VISA: "Visa",
    Master: "Mastercard",
    Mastercard: "Mastercard",
    MasterCard: "Mastercard",
    "Master Card": "Mastercard",
    Discover: "Discover",
    "American Express": "American Express",
    Amex: "American Express",
    DinersClub: "Diners Club",
    "Diners Club": "Diners Club",
    JCB: "JCB",
  };

  return brandMap[brand] || "";
};
